import { byteSize } from "../../common/byte-size";
import type { Fact } from "../../common/fact-table";
import type { Archive, Exchange } from "./parse";

// How a recorded value is spelled on screen. A fact the recording does not carry is written as the empty string
// rather than as a dash, `FactTable` being the thing that takes a row with nothing in it off the card.

export function writeSize(bytes: number | null): string {
  return bytes === null ? "" : byteSize(bytes);
}

// Under a second the millisecond is the number somebody reads; past it, the second is, and three decimal places of
// millisecond on a request that took four seconds is precision nobody asked for.
export function writeMs(ms: number | null): string {
  if (ms === null) return "";
  if (ms < 1) return `${ms.toFixed(2)} ms`;
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}

// An exchange that never got a response has no status to write, `parse.ts` having read the 0 a recorder writes for
// one as the absence it is.
export function writeStatus(exchange: Exchange): string {
  return exchange.status === null ? "—" : String(exchange.status);
}

// Green through red the way every other tool draws them, with a request that failed outright in the grey of a
// response that never arrived.
export function statusColour(status: number | null): string {
  if (status === null) return "gray";
  if (status < 200) return "cyan";
  if (status < 300) return "teal";
  if (status < 400) return "blue";
  if (status < 500) return "yellow";
  return "red";
}

// The line the card is read by: the path with its query, which is the part that differs between two requests to the
// same host, and never the origin repeated forty times down the page.
export function writeTarget(exchange: Exchange): string {
  if (!exchange.host) return exchange.url || "(no URL)";
  const query = exchange.query ? `?${exchange.query}` : "";
  return `${exchange.path || "/"}${query}`;
}

export function writeStarted(exchange: Exchange): string {
  if (exchange.startedAt === null) return exchange.started;
  return new Date(exchange.startedAt).toLocaleString();
}

// Where the exchange sits in the recording, which is the one thing a card cannot say for itself: two requests a
// second apart and two an hour apart look identical otherwise.
export function writeOffset(exchange: Exchange, from: number | null): string {
  if (from === null || exchange.startedAt === null) return "";
  return `+${writeStretch(Math.max(exchange.startedAt - from, 0))}`;
}

export function writeSpan(from: number | null, to: number | null): string {
  if (from === null || to === null) return "";
  return writeStretch(Math.max(to - from, 0));
}

// A request is milliseconds and a recording is however long somebody left the tools open, so a stretch of the second
// kind is said in minutes and hours: a session spelled as 2999.34 s is a number nobody reads as fifty minutes.
function writeStretch(ms: number): string {
  if (ms < 60_000) return writeMs(ms);
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes} min ${Math.round((ms % 60_000) / 1000)} s`;
  return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}

// The phases in the order they happen, which is also the order the bar draws them in; the colours are only ever
// read from here, a bar and a table disagreeing about which colour DNS is being worse than no colour at all.
export const PHASE_COLOUR: Record<string, string> = {
  Blocked: "gray",
  DNS: "grape",
  Connect: "orange",
  TLS: "violet",
  Send: "cyan",
  Wait: "yellow",
  Receive: "teal",
};

// What the Overview tab says about one exchange. `FactTable` drops a row whose value is empty, which is the whole of
// how a recording that carried no server address or no page ends up with no line about either.
export function exchangeFacts(exchange: Exchange, from: number | null): Fact[] {
  return [
    { label: "URL", value: exchange.url },
    { label: "Method", value: exchange.method },
    { label: "Status", value: statusLine(exchange) },
    { label: "HTTP version", value: exchange.httpVersion },
    { label: "Resource type", value: exchange.resourceType },
    { label: "MIME type", value: exchange.mimeType },
    { label: "Redirect to", value: exchange.redirect },
    { label: "Error", value: exchange.error },
    { label: "Server IP", value: exchange.serverIp },
    { label: "Connection", value: exchange.connection },
    { label: "Page", value: exchange.page },
    { label: "Started", value: writeStarted(exchange) },
    { label: "Offset", value: writeOffset(exchange, from) },
    { label: "Duration", value: writeMs(exchange.time) },
    { label: "Request size", value: writeSize(exchange.requestSize) },
    { label: "Transferred", value: writeSize(exchange.transferSize) },
    { label: "Content size", value: writeSize(exchange.contentSize) },
  ];
}

// The two cards over the list: what the file holds, and where it came from. Split so the pair sits abreast rather
// than as one column of nine rows above every request in the recording.
export function fileFacts(archive: Archive, name: string): Fact[] {
  return [
    { label: "Name", value: name },
    { label: "Requests", value: String(archive.exchanges.length) },
    { label: "Transferred", value: writeSize(archive.transferred || null) },
    { label: "Recording", value: writeSpan(archive.startedAt, archive.endedAt) },
  ];
}

export function recorderFacts(archive: Archive): Fact[] {
  return [
    { label: "First request", value: archive.startedAt === null ? "" : new Date(archive.startedAt).toLocaleString() },
    { label: "Pages", value: archive.pages > 0 ? String(archive.pages) : "" },
    { label: "Recorded by", value: archive.creator },
    { label: "Browser", value: archive.browser },
    { label: "HAR version", value: archive.version },
  ];
}

function statusLine(exchange: Exchange): string {
  const status = writeStatus(exchange);
  if (status === "—") return exchange.error ? "No response" : "";
  return exchange.statusText ? `${status} ${exchange.statusText}` : status;
}
